import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react';
import cx from 'classnames';

import { Button } from '@ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { cardHeaderClass, cardHeaderTitleClass, cardShellClass, tableFrameClass } from '@app/constants/styles';
import ConfirmDialog from '../../ConfirmDialog';
import SprintRangePicker from './SprintRangePicker';
import { formatEur } from './budget';
import { formatShortDay, yearOf } from './productVersions';
import { gridHeadText, gridHeadTint, gridPadded } from './sprintGrid';
import {
  overlapping,
  readStatements,
  spanOfSprints,
  sprintLastDay,
  sprintOfDay,
  sprintsBetween,
  statementProblem,
  withoutStatement,
  withStatement,
  writeStatements,
  takenSprintIds,
  type SowSprint,
  type StatementOfWork,
  type StatementProblem,
} from './statementsOfWork';

/** A figure nobody has filled in yet, as everywhere else on these tabs. */
const dash = '—';

/** A fixed width, so the end already picked does not move sideways as the second one arrives. */
const SpanEnd = ({ label, sprint, day }: { label: string; sprint?: number; day?: string }): JSX.Element => {
  const { t } = useTranslation();
  return (
    // Keyed by its own sprint, so closing a span animates only the end that was added.
    <div className="w-24 shrink-0 animate-in fade-in text-center duration-[180ms] motion-reduce:animate-none">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="truncate text-sm font-semibold text-foreground">
        {sprint === undefined ? dash : t('budget.sprintNumber', { number: sprint })}
      </p>
      <p className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{day ?? ''}</p>
    </div>
  );
};

/** Not paged by the tab's year: a statement of work crossing a year boundary is ordinary. */
const StatementOfWorkCard = ({
  productId,
  sprints,
  canEdit,
}: {
  productId: string;
  sprints: SowSprint[];
  /** Read-only without it, as every other card on the page is for whoever cannot edit the product. */
  canEdit: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  // Bumped on save, because the statements live outside React in the product store.
  const [revision, setRevision] = useState(0);
  const [editing, setEditing] = useState<StatementOfWork | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<StatementOfWork | null>(null);
  const [startSprintId, setStartSprintId] = useState('');
  const [endSprintId, setEndSprintId] = useState('');
  const [amount, setAmount] = useState('');
  /** Where somebody is looking, not what they picked: the grid pages while the ends stay put. */
  const [pickerYear, setPickerYear] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statements = useMemo(() => readStatements(productId), [productId, revision]);

  // Local, not UTC: a UTC key leaves a Brussels morning on yesterday's year until 02:00. `en-CA` is ISO.
  const today = new Date().toLocaleDateString('en-CA');

  const spanOf = (sow: StatementOfWork | null): string =>
    sow ? t('statementOfWork.span', { from: formatShortDay(sow.startDate), to: formatShortDay(sow.endDate) }) : '';

  const open = adding || editing !== null;
  // A sprint is picked and its span stored: everything that reads a statement of work reads days.
  const { startDate, endDate } = spanOfSprints(sprints, startSprintId, endSprintId);
  const draft = { id: editing?.id, startDate, endDate, amount };
  const problem = statementProblem(statements, draft);
  // Nothing typed yet is not a complaint: Save is held until all three are in.
  const reasons: Record<StatementProblem, string> = {
    noStart: '',
    noEnd: '',
    // Nothing typed yet says nothing; a figure the dialog refuses has to say why Save is grey.
    noAmount: amount.trim() === '' ? '' : t('statementOfWork.problems.badAmount'),
    backwards: t('statementOfWork.problems.backwards'),
    overlaps: t('statementOfWork.problems.overlaps', { span: spanOf(overlapping(statements, draft)) }),
  };

  // Not this one's own sprints while it is being edited: it is sitting on them.
  const taken = takenSprintIds(sprints, statements, editing?.id);

  /** A span drawn on the grid, which is the only way one is picked. */
  const pickSpan = (nextStartId: string, nextEndId: string): void => {
    setStartSprintId(nextStartId);
    setEndSprintId(nextEndId);
  };

  // Written out under the grid: a cell forty-eight pixels wide can say neither.
  const first = sprints.find(sprint => sprint.id === startSprintId);
  const last = sprints.find(sprint => sprint.id === endSprintId);
  const sprintsPicked = first && last ? sprintsBetween(sprints, first.id, last.id).length : 0;

  const close = (): void => {
    setAdding(false);
    setEditing(null);
  };

  const openAdd = (): void => {
    // The year being worked in, so the list opens on the sprints anyone is signing for.
    const yearsHeld = [...new Set(sprints.map(sprint => yearOf(sprint.startDate)))].sort();
    const thisYear = yearsHeld.includes(yearOf(today)) ? yearOf(today) : (yearsHeld[0] ?? '');
    setPickerYear(thisYear);
    setStartSprintId('');
    setEndSprintId('');
    setAmount('');
    setAdding(true);
  };

  const openEdit = (sow: StatementOfWork): void => {
    // One saved before the dialog asked for sprints holds free dates, and saving snaps it.
    const from = sprintOfDay(sprints, sow.startDate);
    const to = sprintOfDay(sprints, sow.endDate);
    setStartSprintId(from?.id ?? '');
    setEndSprintId(to?.id ?? '');
    setPickerYear(from ? yearOf(from.startDate) : '');
    setAmount(String(sow.amount));
    setEditing(sow);
  };

  // One write for both dialogs and for the delete: the list is rewritten whole either way.
  const store = (next: StatementOfWork[]): void => {
    writeStatements(productId, next);
    close();
    setRevision(current => current + 1);
  };

  return (
    <section className={cardShellClass}>
      <div className={cx(cardHeaderClass, 'print:hidden')}>
        <div className={cardHeaderTitleClass}>
          <h2 className="text-base font-semibold text-foreground">{t('statementOfWork.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('statementOfWork.hint')}</p>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" className="shrink-0 bg-card shadow-xs" onClick={openAdd}>
            <Plus className="size-4" />
            {t('statementOfWork.add')}
          </Button>
        )}
      </div>

      {!statements.length ? (
        <p className="px-5 max-md:px-4 pb-5 text-sm text-muted-foreground">{t('statementOfWork.none')}</p>
      ) : (
        <div className={cx(tableFrameClass, 'mb-5')}>
          {/* The last row's bottom border is the wrapper's, and the last cell's right border too. */}
          <table className="w-full table-fixed border-separate border-spacing-0 [&_tr>*:last-child]:border-r-0 [&>tbody>tr:last-child>*]:border-b-0">
            <thead>
              <tr>
                <th scope="col" className={cx(gridHeadText, gridHeadTint, 'text-left')}>
                  {t('statementOfWork.period')}
                </th>
                <th scope="col" className={cx(gridHeadText, gridHeadTint, 'w-44 text-right')}>
                  {t('statementOfWork.amount')}
                </th>
                {canEdit && (
                  <th scope="col" className={cx(gridHeadText, gridHeadTint, 'w-24')}>
                    <span className="sr-only">{t('statementOfWork.rowActions')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {statements.map(sow => (
                <tr key={sow.id}>
                  <th scope="row" className={`${gridPadded} text-left text-sm font-normal text-foreground`}>
                    <span className="block truncate">{spanOf(sow)}</span>
                  </th>
                  <td className={`${gridPadded} text-right`}>
                    <span className="whitespace-nowrap text-sm font-medium tabular-nums text-foreground">
                      {formatEur(sow.amount)}
                    </span>
                  </td>
                  {canEdit && (
                    <td className={`${gridPadded} text-right`}>
                      {/* Two buttons rather than a menu, as the chapters admin table offers the same pair. */}
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('statementOfWork.editItem', { span: spanOf(sow) })}
                          onClick={() => openEdit(sow)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('statementOfWork.deleteItem', { span: spanOf(sow) })}
                          onClick={() => setRemoving(sow)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={next => !next && close()}>
        {/* Two panels of seven sprint columns and a little air: measured, not guessed. */}
        <DialogContent className="sm:max-w-[52rem]">
          <DialogHeader>
            <DialogTitle>{editing ? t('statementOfWork.editTitle') : t('statementOfWork.newTitle')}</DialogTitle>
            <DialogDescription>{t('statementOfWork.dialogHint')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {/* The span drawn on the sprints themselves, rather than on a pair of dropdowns. */}
            <SprintRangePicker
              sprints={sprints}
              hint={t('statementOfWork.pickerHint')}
              leftYear={pickerYear}
              onLeftYear={setPickerYear}
              startId={startSprintId}
              endId={endSprintId}
              taken={taken}
              onPick={pickSpan}
            />
            {/* The span and its ceiling on one row: two stacked blocks left the dialog mostly air. */}
            <div className="flex items-stretch overflow-hidden rounded-xl border border-border bg-muted/30">
              <div className="flex w-64 shrink-0 flex-col justify-center gap-1 border-r border-border px-5 max-md:px-4 py-3">
                <Label htmlFor="sow-amount">{t('statementOfWork.amount')}</Label>
                <Input
                  id="sow-amount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={amount}
                  placeholder={t('budget.amountPlaceholder')}
                  onChange={event => setAmount(event.target.value)}
                  className={cx(
                    'bg-card [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                    problem === 'noAmount' && amount.trim() !== '' && 'border-destructive',
                  )}
                />
                <p className="text-[11px] leading-tight text-muted-foreground">{t('statementOfWork.amountHint')}</p>
              </div>
              {/* Centred rather than left-packed: the two ends and the length read as one thing. */}
              <div className="flex min-h-[5.25rem] flex-1 items-center justify-center gap-6 px-5 max-md:px-4 py-3">
                {!first ? (
                  <p className="animate-in fade-in text-sm text-muted-foreground duration-[180ms] motion-reduce:animate-none">
                    {t('statementOfWork.nonePicked')}
                  </p>
                ) : (
                  <>
                    <SpanEnd
                      // Prefixed: a span of one sprint is both ends, and a duplicate key grew the summary.
                      key={`from-${first.id}`}
                      label={t('statementOfWork.spanFrom')}
                      sprint={first.number}
                      day={formatShortDay(first.startDate)}
                    />
                    <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <SpanEnd
                      key={last ? `to-${last.id}` : 'to-none'}
                      label={t('statementOfWork.spanTo')}
                      sprint={last?.number}
                      day={last && formatShortDay(sprintLastDay(sprints, last))}
                    />
                  </>
                )}
                {/* How long the thing being signed for runs, which is what the two dates are asking. */}
                <div className={cx('w-20 shrink-0', !first && 'hidden')}>
                  {last && (
                    // Keyed by its figure so extending a span says so; a fade, since a slide read as a twitch.
                    <span
                      key={sprintsPicked}
                      className="animate-in fade-in rounded-full bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground duration-[180ms] motion-reduce:animate-none"
                    >
                      {t('statementOfWork.sprintCount', { count: sprintsPicked })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {problem && reasons[problem] && <p className="text-sm text-destructive">{reasons[problem]}</p>}
            {/* Said in here: the dialog stays open on a refused write, so the card has nothing to say. */}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>
              {t('actions.cancel')}
            </Button>
            <Button disabled={!!problem} onClick={() => store(withStatement(statements, draft))}>
              {editing ? t('actions.save') : t('statementOfWork.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={next => !next && setRemoving(null)}
        title={t('statementOfWork.deleteTitle')}
        description={t('statementOfWork.deleteHint', {
          span: spanOf(removing),
          amount: removing ? formatEur(removing.amount) : '',
        })}
        onConfirm={() => removing && store(withoutStatement(statements, removing.id))}
      />
    </section>
  );
};

export default StatementOfWorkCard;
